import React, { useEffect, useState } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

let listeners: ((toast: Toast) => void)[] = [];
let nextId = 0;

export function showToast(message: string, type: ToastType = 'info') {
  const toast: Toast = { id: ++nextId, message, type };
  listeners.forEach(fn => fn(toast));
}

const icons: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle size={18} className="text-emerald-600" />,
  error: <AlertCircle size={18} className="text-red-600" />,
  info: <Info size={18} className="text-primary" />,
};

const bgColors: Record<ToastType, string> = {
  success: 'bg-emerald-50 border-emerald-200',
  error: 'bg-red-50 border-red-200',
  info: 'bg-sky-50 border-sky-200',
};

export default function ToastContainer() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    const handler = (toast: Toast) => {
      setToasts(prev => [...prev, toast]);
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== toast.id));
      }, 4000);
    };
    listeners.push(handler);
    return () => { listeners = listeners.filter(l => l !== handler); };
  }, []);

  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2" role="log">
      {toasts.map(t => (
        <div
          key={t.id}
          className={`
            flex items-center gap-3 px-4 py-3 rounded-card border shadow-soft
            animate-slide-in font-sans text-sm ${bgColors[t.type]}
          `}
        >
          {icons[t.type]}
          <span className="text-charcoal font-medium flex-1">{t.message}</span>
          <button
            onClick={() => setToasts(prev => prev.filter(tt => tt.id !== t.id))}
            className="text-muted hover:text-charcoal"
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
